import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { frsChanged, dismissFrsChanged } from "../offline/interceptors";

/** Project-level "Needs review": offline writes the server refused, with one-tap resolution, plus
 *  the "FRS changed while you were offline" banner. */
export default function NeedsReview({ projectId }) {
  const [rows, setRows] = useState([]);
  const [frs, setFrs] = useState(null);
  const load = useCallback(() => {
    api.get("/offline/conflicts", { params: { project_id: projectId }, _noCache: true }).then(({ data }) => setRows(data.conflicts || [])).catch(() => {});
    frsChanged(projectId).then((v) => setFrs(v || null));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (c, resolution) => {
    try {
      if (resolution === "applied_mine") {
        const url = c.path.replace(/^\/api\/field/, "");
        const res = await api.request({ method: c.method, url, data: c.payload, headers: { "X-Client-Write-Id": crypto.randomUUID() }, validateStatus: () => true, _noQueue: true });
        if (res.status >= 300) { toast.error(`Still refused: ${typeof res.data?.detail === "string" ? res.data.detail : res.status}`); return; }
      }
      await api.post(`/offline/conflicts/${c.id}/resolve`, { resolution }, { _noQueue: true });
      toast.success(resolution === "keep_server" ? "Server version kept" : "Applied as recorded");
      load();
    } catch (e) { toast.error("Could not resolve"); }
  };
  if (!rows.length && !frs) return null;
  return (
    <div className="space-y-2" data-testid="needs-review">
      {frs && (
        <div className="flex items-center gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-sm" data-testid="frs-changed-banner">
          <span className="font-medium text-orange-900">FRS changed while you were offline — review the rooms you worked on.</span>
          <button type="button" onClick={() => dismissFrsChanged(projectId).then(() => setFrs(null))} className="ml-auto text-xs underline text-orange-800" data-testid="frs-changed-dismiss">Reviewed</button>
        </div>
      )}
      {rows.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-white" data-testid="needs-review-list">
          <div className="px-3 py-2 border-b border-red-100 text-sm font-semibold text-red-800">Needs review · {rows.length} offline write{rows.length === 1 ? "" : "s"} the server refused</div>
          {rows.map((c) => (
            <div key={c.id} className="px-3 py-2 border-b border-neutral-100 text-xs space-y-1" data-testid={`review-${c.id}`}>
              <div><b>{c.kind || c.path.split("/").slice(-1)[0]}</b> · {c.user} · recorded {String(c.client_timestamp || "").slice(0, 16).replace("T", " ")}</div>
              <div className="text-red-700">Server: {typeof c.server_detail === "string" ? c.server_detail : JSON.stringify(c.server_detail).slice(0, 160)}</div>
              <div className="text-neutral-600">Mine: {JSON.stringify(c.payload).slice(0, 160)}</div>
              <div className="flex gap-1">
                <button type="button" onClick={() => resolve(c, "applied_mine")} className="px-2 py-1 rounded bg-neutral-900 text-white" data-testid={`review-apply-${c.id}`}>Apply mine</button>
                <button type="button" onClick={() => resolve(c, "keep_server")} className="px-2 py-1 rounded border" data-testid={`review-keep-${c.id}`}>Keep server</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
